/**
 * Cập nhật PWA lên bản mới.
 *
 * Trình duyệt chỉ tự kiểm tra service worker mới khi điều hướng trang. PWA trên iOS mở lại từ nền
 * (không điều hướng) nên có thể kẹt ở bản cũ rất lâu — phải chủ động gọi registration.update()
 * mỗi khi app quay lại foreground và theo chu kỳ.
 */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

type Updatable = Pick<ServiceWorkerRegistration, 'update'>;

const checkForUpdate = (registration: Updatable) => {
  registration.update().catch(() => {
    // Mất mạng/đang offline: lần quay lại foreground hoặc chu kỳ sau sẽ thử lại.
  });
};

/** Trả về hàm dừng theo dõi. */
export const watchForUpdates = (
  registration: Updatable,
  doc: Document = document,
  intervalMs: number = UPDATE_CHECK_INTERVAL_MS,
): (() => void) => {
  const onVisibilityChange = () => {
    if (doc.visibilityState === 'visible') checkForUpdate(registration);
  };

  doc.addEventListener('visibilitychange', onVisibilityChange);
  const timer = setInterval(() => checkForUpdate(registration), intervalMs);

  return () => {
    doc.removeEventListener('visibilitychange', onVisibilityChange);
    clearInterval(timer);
  };
};

/** Cho service worker mới chạy ngay (SKIP_WAITING — xem service-worker.ts) rồi tải lại trang. */
export const activateWaitingWorker = (
  registration: ServiceWorkerRegistration,
  reload: () => void = () => window.location.reload(),
) => {
  const waiting = registration.waiting;
  if (!waiting) return;

  waiting.addEventListener('statechange', (event) => {
    if ((event.target as ServiceWorker).state === 'activated') reload();
  });
  waiting.postMessage({ type: 'SKIP_WAITING' });
};

interface HandleUpdateOptions {
  doc?: Document;
  /** Hỏi người dùng; gọi `apply` khi họ đồng ý cập nhật ngay. */
  prompt: (apply: () => void) => void;
  reload?: () => void;
}

/**
 * Có bản mới đã tải xong:
 * - app đang ẩn → cập nhật luôn (người dùng không thấy reload);
 * - app đang dùng → hỏi trước, không reload ngang làm mất dữ liệu đang nhập; nếu họ chưa bấm
 *   thì tự cập nhật lần kế tiếp app bị ẩn (chuyển app / khoá màn hình).
 */
export const handleUpdateFound = (
  registration: ServiceWorkerRegistration,
  { doc = document, prompt, reload }: HandleUpdateOptions,
) => {
  let applied = false;
  const apply = () => {
    if (applied) return;
    applied = true;
    doc.removeEventListener('visibilitychange', onHidden);
    activateWaitingWorker(registration, reload);
  };
  function onHidden() {
    if (doc.visibilityState === 'hidden') apply();
  }

  if (doc.visibilityState === 'hidden') {
    apply();
    return;
  }

  doc.addEventListener('visibilitychange', onHidden);
  prompt(apply);
};
